import * as React from 'react';

import { getIntegrationBlockInSet } from '@gitbook/app-database';
import type { BlockIntegrationData } from '@gitbook/doc-core';
import type { BlockProps } from '@gitbook/slate-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { useNavigation, useNavigationState } from '../../../navigation';
import { apiResource, readAPICache } from '../../../state';
import { IntegrationIcon } from '../..';
import {
    BlockLoadingFallback,
    BlockMissingContentAlert,
    BlockWrapper,
    useBlockLayoutGroupFactory,
    useBlockPaletteFactory,
    VoidBlockFrame,
} from '../common';
import { EmbedView } from './EmbedView';
import { useSpaceIntegrationsBlocks } from './IntegrationBlocks';
import { IntegrationBlockView, type IntegrationBlockViewRef } from './IntegrationBlockView';
import { MisconfiguredIntegrationAlert } from './MisconfiguredIntegrationAlert';

/**
 * Component to render an integration block.
 * If the integration is not installed, it can fallback to an embed block.
 */
export function BlockIntegration(props: BlockProps): React.ReactElement {
    const { node, editor } = props;
    const { readOnly } = editor;
    const { screen } = useNavigationState();

    const integrationBlockViewRef = React.useRef<IntegrationBlockViewRef>(null);
    const { navigate } = useNavigation();

    const spaceIntegrationsBlocks = useSpaceIntegrationsBlocks();

    const nodeKey = node.key;
    const data = node.data.toFrozenObject<BlockIntegrationData>();

    const createLayoutGroup = useBlockLayoutGroupFactory({ nodeKey });

    const palette = useBlockPaletteFactory({
        nodeKey,
        createExtraGroups: () => {
            return [
                ...(integrationBlockViewRef?.current?.getPaletteGroups() || []),
                {
                    key: 'integration',
                    commands: () => {
                        const integration = readAPICache(
                            apiResource(
                                'getIntegrationByName',
                                { integrationName: data.integration },
                                { nullOnError: true }
                            )
                        );

                        if (!integration) {
                            return [];
                        }

                        return [
                            {
                                key: 'integration.settings',
                                icon: <IntegrationIcon integration={integration} />,
                                title: `Configure ${integration.title}`,
                                trigger: () => {
                                    if (screen.type === 'space' || screen.type === 'site') {
                                        navigate({
                                            current: {
                                                sideSheet: {
                                                    type: 'integrations',
                                                    view: {
                                                        type: 'integration',
                                                        integration: integration.name,
                                                    },
                                                },
                                            },
                                        });
                                    } else {
                                        navigate({
                                            screen: {
                                                type: 'integration',
                                                integration: integration.name,
                                            },
                                        });
                                    }
                                },
                            },
                        ];
                    },
                },
                createLayoutGroup(),
            ].filter(filterOutNullable);
        },
    });

    // If the integration is not configured for the space, we fallback to an embed block
    // or we display an alert to configure the integration.
    const integrationBlock = React.useMemo(() => {
        return getIntegrationBlockInSet(spaceIntegrationsBlocks, data.integration, data.block);
    }, [spaceIntegrationsBlocks, data.integration, data.block]);
    if (!integrationBlock) {
        return (
            <BlockWrapper {...props} palette={palette}>
                <VoidBlockFrame {...props}>
                    <React.Suspense fallback={<BlockLoadingFallback />}>
                        {data.url ? (
                            <EmbedView url={data.url} readOnly={readOnly} />
                        ) : readOnly ? (
                            <BlockMissingContentAlert />
                        ) : (
                            <MisconfiguredIntegrationAlert integrationName={data.integration} />
                        )}
                    </React.Suspense>
                </VoidBlockFrame>
            </BlockWrapper>
        );
    }

    return (
        <BlockWrapper {...props} palette={palette}>
            <VoidBlockFrame {...props}>
                <React.Suspense fallback={<BlockLoadingFallback />}>
                    <IntegrationBlockView
                        ref={integrationBlockViewRef}
                        data={data}
                        readOnly={readOnly}
                        onDataChange={(newData) => {
                            editor.change((change) => {
                                change.setNodeByKey(node.key, {
                                    data: newData,
                                });
                            });
                        }}
                    />
                </React.Suspense>
            </VoidBlockFrame>
        </BlockWrapper>
    );
}
